/**
 * Parmco Delivery Details — accordion helper
 * Version: 1.7.0
 *
 * READ THIS BEFORE CHANGING ANYTHING HERE
 * ---------------------------------------
 * This script deliberately does NOT intercept form submission.
 *
 * This store has Shopify spam protection active on the contact form.
 * A programmatic POST to /contact is rejected outright:
 *
 *     400  "Missing CAPTCHA token"
 *
 * The CAPTCHA token is injected by Shopify's own script during a *native*
 * form submission. Calling preventDefault() and submitting via fetch() or
 * a hidden iframe skips that injection and the enquiry is silently lost.
 * (A hidden iframe is impossible here anyway — the storefront sends
 * X-Frame-Options: DENY and CSP frame-ancestors 'none'.)
 *
 * Earlier versions of this file did exactly that and would have dropped
 * every submission. If you want an inline, no-reload form, the correct
 * route is the Globo / Powerful Form Builder app already installed on this
 * store, not hand-rolled AJAX against /contact.
 *
 * What this script actually does:
 *   1. After the page returns from a submission, re-opens the accordion
 *      and scrolls to the server-rendered result (the accordion is closed
 *      by default, so otherwise the shopper would never see it).
 *   2. Gives the submit button a "Sending…" state — without cancelling
 *      the native submit.
 *   3. Strips the shopper's submitted details back out of the URL, which
 *      Shopify echoes into the query string when it rejects a submission.
 *   4. Opens the accordion when the relabelled "Delivery details required"
 *      button on Parmco products is clicked.
 *
 * No PII is ever written to the console — only context labels and status.
 */

(function () {
  'use strict';

  var SCRIPT_VERSION = '1.7.0';
  var LOG_PREFIX = '[ParmcoDelivery v' + SCRIPT_VERSION + ']';

  // How long to wait for the native submit to navigate away before assuming
  // it failed and handing control back to the shopper.
  var STALL_TIMEOUT_MS = 15000;

  /**
   * initParmcoDelivery
   * Entry point. Handles every Parmco delivery block on the page (the
   * product page renders one, the cart page renders one).
   */
  function initParmcoDelivery() {
    var wrappers = document.querySelectorAll('[data-parmco-delivery-context]');
    console.log(LOG_PREFIX, 'init — found', wrappers.length, 'block(s) on this page');

    Array.prototype.forEach.call(wrappers, function (wrapper) {
      revealServerResponse(wrapper);
      wireUpSubmitFeedback(wrapper);
    });

    if (wrappers.length) scrubDeliveryDetailsFromUrl();

    wireUpJumpLinks();
  }

  /**
   * wireUpJumpLinks
   * On Parmco products the "Enquire about price and availability" button is
   * relabelled "Delivery details required" and points at this accordion
   * (see sections/main-product.liquid). A bare fragment link would scroll to
   * a <details> that is still collapsed, so intercept the click, open it,
   * and put the cursor in the first field.
   *
   * If the accordion is not on the page for any reason, we do NOT
   * preventDefault — the browser falls back to the plain fragment jump.
   */
  function wireUpJumpLinks() {
    var links = document.querySelectorAll('[data-parmco-delivery-jump]');
    if (!links.length) return;

    Array.prototype.forEach.call(links, function (link) {
      link.addEventListener('click', function (event) {
        var wrapper = document.querySelector('[data-parmco-delivery-context]');
        var details = wrapper && wrapper.querySelector('details');
        if (!details) {
          console.warn(LOG_PREFIX, 'jump link clicked but no delivery accordion on this page');
          return;
        }

        event.preventDefault();
        details.open = true;

        try {
          details.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) {
          details.scrollIntoView();
        }

        // Wait for the smooth scroll before focusing, otherwise the focus
        // jump fights the animation.
        window.setTimeout(function () {
          var first = details.querySelector('input:not([type=hidden]), textarea');
          if (!first) return;
          try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
        }, 450);

        console.log(LOG_PREFIX, 'jump link opened the delivery accordion');
      });
    });
  }

  /**
   * scrubDeliveryDetailsFromUrl
   * PRIVACY. When Shopify rejects a contact submission (e.g. a malformed
   * email), it redirects back with every *custom* field echoed into the
   * query string. Verified on this store:
   *
   *   /pages/contact-us?contact%5BName%5D=...&contact%5BPhone+number%5D=...&form_type=contact
   *
   * For this form that would put the shopper's phone, delivery address,
   * suburb, city and postcode in the address bar — and therefore in browser
   * history, in anything they copy or share, and in any analytics that logs
   * full URLs.
   *
   * The page has already rendered from those params by the time this runs,
   * so replacing the history entry costs nothing and takes the address out
   * of the URL and out of history.
   *
   * This is a mitigation, not a guarantee: an analytics beacon that fires
   * before this script may already have captured the URL.
   */
  function scrubDeliveryDetailsFromUrl() {
    if (!window.history || !window.history.replaceState) return;
    if (window.location.search.indexOf('contact%5B') === -1 &&
        window.location.search.indexOf('contact[') === -1) return;

    try {
      window.history.replaceState({}, document.title, window.location.pathname);
      console.log(LOG_PREFIX, 'cleared submitted field values from the URL');
    } catch (e) {
      console.warn(LOG_PREFIX, 'could not clear the URL —', e && e.message);
    }
  }

  /**
   * revealServerResponse
   * Shopify redirects back to this page after the contact form is posted,
   * and Liquid renders either the success panel or the error panel inside
   * the accordion. The accordion is collapsed by default, so open it and
   * bring the message into view.
   * @param {HTMLElement} wrapper
   */
  function revealServerResponse(wrapper) {
    var status = wrapper.querySelector('.form-status');
    if (!status) return;

    var context = wrapper.getAttribute('data-parmco-delivery-context');
    var isError = status.classList.contains('form-status-error');

    var details = wrapper.querySelector('details');
    if (details) details.open = true;

    console.log(LOG_PREFIX, 'server response shown — context:', context, '— type:', isError ? 'error' : 'success');

    // Let the accordion finish opening before measuring scroll position.
    window.setTimeout(function () {
      try {
        status.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        status.scrollIntoView();
      }
      try {
        status.focus({ preventScroll: true });
      } catch (e) {
        status.focus();
      }
    }, 0);
  }

  /**
   * wireUpSubmitFeedback
   * Shows a "Sending…" state on submit. This listener must never call
   * preventDefault() — see the note at the top of this file.
   * @param {HTMLElement} wrapper
   */
  function wireUpSubmitFeedback(wrapper) {
    var form = wrapper.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', function () {
      var button = form.querySelector('.parmco-delivery__submit');
      if (!button) return;

      // Deferred to the next tick: disabling a submit button synchronously
      // inside its own submit handler can cancel the submission in some
      // browsers. By then the native POST is already on its way.
      window.setTimeout(function () {
        button.disabled = true;
        button.textContent = 'Sending…';
      }, 0);

      // Safety net. A native submit normally navigates away, so this timer
      // dies with the page. If we are still here afterwards the submission
      // did not proceed — most often because Shopify's hCaptcha declined to
      // issue a token — and leaving the button disabled would trap the
      // shopper on a permanent "Sending…" with no way to retry.
      window.setTimeout(function () {
        if (!button.disabled) return;
        button.disabled = false;
        button.textContent = 'Send delivery details';
        showStalledMessage(form);
        console.warn(LOG_PREFIX, 'submission did not proceed within ' + (STALL_TIMEOUT_MS / 1000) + 's — button re-enabled so the shopper can retry');
      }, STALL_TIMEOUT_MS);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initParmcoDelivery);
  } else {
    initParmcoDelivery();
  }
})();
